import { useState } from 'react'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { formatCents, formatDate, formatTime } from '../../lib/format.ts'
import { mutators } from '../../zero/mutators.ts'
import { queries } from '../../zero/queries.ts'
import { PagedSection, usePagedLimit } from './PagedSection.tsx'

/** Every non-deleted fee (US-20). Admins may soft-delete an unpaid one. */
export function FeeHistory({ userId, canRemove }: { userId: string; canRemove: boolean }) {
  const z = useZero()
  const { limit, showMore } = usePagedLimit()
  const [fees, result] = useQuery(queries.feeHistoryForUser({ userId, limit }))
  const [message, setMessage] = useState('')

  async function removeFee(id: string) {
    if (!window.confirm('Remove this outstanding fee?')) return
    setMessage('')
    try {
      const mutation = z.mutate(mutators.fees.remove({ id }))
      const clientResult = await mutation.client
      if (clientResult.type === 'error') {
        setMessage(clientResult.error.message)
        return
      }
      setMessage('Fee removed · syncing…')
      void mutation.server.then((serverResult) => {
        setMessage(serverResult.type === 'success' ? 'Fee removed' : serverResult.error.message)
      })
    } catch {
      setMessage('Failed to remove fee')
    }
  }

  return (
    <PagedSection
      title="Fees"
      shown={fees.length}
      limit={limit}
      showMore={showMore}
      complete={result.type === 'complete'}
      emptyText="No fees recorded."
      footer={message ? <p className="form-message">{message}</p> : null}
    >
      {fees.map((fee) => {
        const paid = fee.paidAt != null
        return (
          <div className="line" key={fee.id}>
            <div className="line-main">
              <div className="line-title">
                {fee.type}
                <span className={`pill-tag ${paid ? '' : 'pill-tag--owing'}`}>
                  {paid ? 'Paid' : 'Outstanding'}
                </span>
                {fee.description ? <span className="muted"> · {fee.description}</span> : null}
              </div>
              <div className="line-sub">
                Fee date {formatDate(fee.feeDate)} · created {formatTime(fee.createdAt)}
              </div>
            </div>
            <span className="line-amount">{formatCents(fee.amountCents)}</span>
            {canRemove && !paid ? (
              <button className="btn-ghost" onClick={() => removeFee(fee.id)} title="Remove fee">
                ✕
              </button>
            ) : null}
          </div>
        )
      })}
    </PagedSection>
  )
}
